import { __rest } from '../../_virtual/_tslib.js';
import React from 'react';
import PropTypes from 'prop-types';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import '../../packages/props-utils/prop-types.js';
import { useComponentId } from '../../hooks/_use-component-id/use-component-id.js';
import { AutocompleteContextProvider } from './state/autocomplete-context.js';
import ListBox, { AutocompleteListBoxPropTypes } from './list-box/list-box.js';
import Combobox, { AutocompleteComboboxPropTypes } from './combobox/combobox.js';
import { ListBoxOptionPropType, ListBoxGroupPropType } from './list-box/quarks/prop-types.js';
import { defaultAutocompleteFilter } from './utils/default-filter.js';
import styles from './autocomplete.scss.js';
import { autocompleteReducer, createAutocompleteInitialState } from './state/autocomplete-reducer.js';
import { optionsHash, isGrouped, isSelected } from './list-box/list-box.utils.js';
import { useIsFirstRender } from '../../hooks/use-is-first-render/use-is-first-render.js';
import { Injecss } from '../../packages/injecss/injecss.js';
import { useBrowser } from '../../hooks/use-browser/use-browser.js';

const bem = new HoneyBem('autocomplete', styles);
/**
 * @since 12.0.0
 *
 * @experimental This component is still undergoing tests and we are actively working on it.
 * This means it **might** receive some changes in the near future, but it is nonetheless fully
 * ready to use. We welcome your feedback!
 *
 * If you are still not comfortable with this prospect, or not ready yet to migrate, you may use the
 * [LegacyAutocomplete](#/Legacy/LegacyAutocomplete) instead.
 */
function Autocomplete(_a) {
  var {
      extraClasses,
      id,
      innerRef: innerRefProp,
      expanded: expandedProp,
      options: optionsProp,
      value: valueProp,
      defaultValue = [],
      inputValue: inputValueProp,
      defaultInputValue = '',
      multiple = false,
      debounce = 300,
      listBoxProps,
      comboboxProps,
      onChange,
      onToggle,
      onDebounce = defaultAutocompleteFilter
    } = _a,
    rest = __rest(_a, ["extraClasses", "id", "innerRef", "expanded", "options", "value", "defaultValue", "inputValue", "defaultInputValue", "multiple", "debounce", "listBoxProps", "comboboxProps", "onChange", "onToggle", "onDebounce"]);
  const [win] = useBrowser();
  const componentId = id || useComponentId('hcr-autocomplete');
  const innerRef = React.useRef(null);
  const timerRef = React.useRef();
  // providing a default value instead of specifying it via prop defaults
  // this prevents infinite re-renders of the component when no options were specified
  const normalizedOptionsProp = optionsProp !== null && optionsProp !== void 0 ? optionsProp : [];
  // options stuff
  const [options, setOptions] = React.useState(normalizedOptionsProp);
  // sync internal options state with the prop,
  // uses custom hashing function to ensure proper dependency checks
  React.useEffect(() => {
    setOptions(normalizedOptionsProp);
  }, [optionsHash(normalizedOptionsProp)]);
  const flatOptions = React.useMemo(() => {
    if (isGrouped(options)) {
      return options.flatMap(group => group.options);
    }
    return options;
  }, [options]);
  // combobox stuff
  const comboboxId = "combobox-".concat(componentId);
  const comboboxLabelId = "label-".concat(comboboxId);
  const comboboxFieldRef = React.useRef(null);
  // list-box stuff
  const listBoxId = "list-box-".concat(componentId);
  const [listBoxTop, setListBoxTop] = React.useState();
  const [state, dispatch] = React.useReducer(autocompleteReducer, {
    value: defaultValue,
    inputValue: defaultInputValue
  }, createAutocompleteInitialState);
  const selectedOptions = valueProp !== null && valueProp !== void 0 ? valueProp : state.value;
  const inputValue = inputValueProp !== null && inputValueProp !== void 0 ? inputValueProp : state.inputValue;
  const expanded = expandedProp !== null && expandedProp !== void 0 ? expandedProp : state.expanded;
  const isValueControlled = valueProp !== undefined;
  const isFirstRender = useIsFirstRender();
  // Ensure "controlled" behavior
  // Update state value and inputValue when value prop changes
  React.useEffect(() => {
    if (isValueControlled) {
      dispatch({
        type: 'UPDATE_VALUE',
        value: valueProp
      });
      // reset input value when empty value given
      // or on every value change when multiple variation is used
      if (!valueProp.length || multiple) {
        dispatch({
          type: 'UPDATE_INPUT_VALUE',
          inputValue: '',
          expanded: state.expanded
        });
      }
      if (!multiple && valueProp.length > 0) {
        dispatch({
          type: 'UPDATE_INPUT_VALUE',
          inputValue: valueProp[0].title,
          expanded: state.expanded
        });
      }
    }
  }, [isValueControlled, JSON.stringify(valueProp)]);
  // run onchange when the state value is updated
  React.useEffect(() => {
    if (isFirstRender) return;
    onChange === null || onChange === void 0 ? void 0 : onChange(state.value, state.latestEvent);
  }, [JSON.stringify(state.value)]);
  React.useEffect(() => {
    if (isFirstRender) return;
    onToggle === null || onToggle === void 0 ? void 0 : onToggle(expanded, state.latestEvent);
  }, [expanded]);
  /** Handles option selection depending if component allows multiple selection or not. */
  const selectOption = (option, event) => {
    if (!multiple) {
      dispatch({
        type: 'SELECT_OPTION',
        option,
        latestEvent: event
      });
      return;
    }
    if (isSelected(option, selectedOptions)) {
      dispatch({
        type: 'REMOVE_OPTION',
        option,
        latestEvent: event
      });
    } else {
      dispatch({
        type: 'ADD_OPTION',
        option,
        latestEvent: event
      });
    }
  };
  /**
   * Handles the key down event on the combobox field, allowing the user to navigate through the
   * options and select them.
   */
  const handleComboboxKeyDown = event => {
    var _a, _b, _c, _d;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      dispatch({
        type: 'EXPAND',
        latestEvent: event
      });
      (_a = comboboxFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      const currFocusedOptionIndex = flatOptions.findIndex(_option => {
        var _a;
        return _option.value === ((_a = state.focusedOption) === null || _a === void 0 ? void 0 : _a.value);
      });
      let nextFocusedOptionIndex = 0;
      // if there is no focused option, e.g.: component was initially closed
      if (currFocusedOptionIndex === -1) {
        nextFocusedOptionIndex = event.key === 'ArrowDown' ? 0 // gets index 0 for first option if key is ArrowDown
        : flatOptions.length - 1; // or last option index if key is ArrowUp
      } else {
        // if component was already opened, cycle through options up or down, depending on key
        if (event.key === 'ArrowDown') {
          nextFocusedOptionIndex = currFocusedOptionIndex === flatOptions.length - 1 ? 0 // gets index 0 if was focused on the last option and key is ArrowDown
          : currFocusedOptionIndex + 1; // or increase the index
        }
        if (event.key === 'ArrowUp') {
          nextFocusedOptionIndex = currFocusedOptionIndex === 0 ? flatOptions.length - 1 // gets last option index if the first option was focused
          : currFocusedOptionIndex - 1; // or decrease the index
        }
      }
      const nextFocusedOption = flatOptions[nextFocusedOptionIndex];
      // sets focus on next option and enables keyboard navigation flag
      dispatch({
        type: 'FOCUS_OPTION',
        option: nextFocusedOption,
        kbNavigation: true,
        latestEvent: event
      });
    }
    if (event.key === 'Enter' && expanded) {
      const optionSelected = (_b = state.focusedOption) !== null && _b !== void 0 ? _b : flatOptions[0];
      // only select options if we have any
      if (optionSelected) {
        selectOption(optionSelected, event);
      }
      event.preventDefault(); // prevent form submission when selecting an option
    }
    if (event.key === 'Backspace' && multiple && inputValue === '') {
      const lastAddedOption = selectedOptions[selectedOptions.length - 1];
      if (lastAddedOption) {
        dispatch({
          type: 'REMOVE_OPTION',
          option: lastAddedOption,
          latestEvent: event
        });
      }
    }
    if (event.key === 'Escape') {
      const setInputValue = multiple ? '' : (_c = selectedOptions[0]) === null || _c === void 0 ? void 0 : _c.title;
      dispatch({
        type: 'COLLAPSE',
        inputValue: setInputValue,
        latestEvent: event
      });
      (_d = comboboxFieldRef.current) === null || _d === void 0 ? void 0 : _d.focus();
    }
  };
  /**
   * Handles the input change event, debouncing the input value to filter the options.
   */
  const handleComboboxChange = event => {
    if (timerRef.current) win === null || win === void 0 ? void 0 : win.clearTimeout(timerRef.current);
    timerRef.current = win === null || win === void 0 ? void 0 : win.setTimeout(() => {
      // Promise.resolve makes sure onDebounce call returns a promise,
      // converting a simple function to a promise call if needed
      Promise.resolve(onDebounce(event.target.value, normalizedOptionsProp)).then(filteredOptions => {
        // do nothing if nothing is returned from the promise,
        // in this case we expect options update via external state and component re-rendering
        if (filteredOptions) {
          setOptions(filteredOptions);
        }
      });
    }, debounce);
    dispatch({
      type: 'UPDATE_INPUT_VALUE',
      inputValue: event.target.value
    });
    if (!multiple && event.target.value === '') {
      dispatch({
        type: 'RESET',
        latestEvent: event
      });
    }
  };
  /**
   * A hacky way of calculating ListBox top position every time the ref callback being called,
   * which means every time the combobox mounts.
   * Passing selectedOptions and expanded state makes top position change
   * only when these values change, this assumes it's the only was for combobox height to change.
   * @param container
   */
  const updateListBoxPosition = React.useCallback(container => {
    if (container !== null) {
      const {
        offsetTop,
        offsetHeight
      } = container;
      const newListBoxTop = offsetTop + offsetHeight;
      // only update state when the number is different
      if (listBoxTop !== newListBoxTop) {
        setListBoxTop(newListBoxTop);
      }
    }
  }, [selectedOptions, expanded]);
  const optionIdTemplate = optionValue => "option-".concat(componentId, "-").concat(optionValue);
  return /*#__PURE__*/React.createElement(AutocompleteContextProvider, {
    state: state,
    dispatch: dispatch,
    comboboxFieldRef: comboboxFieldRef,
    selectOption: selectOption
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    id: componentId,
    ref: element => {
      reactUtils.assignInnerRefWithComponentRef(element, innerRefProp, innerRef);
    },
    className: bem.b({}, extraClasses)
  }, rest, {
    onBlur: event => {
      var _a, _b, _c;
      (_a = rest.onBlur) === null || _a === void 0 ? void 0 : _a.call(rest, event);
      if (!((_b = innerRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.relatedTarget))) {
        const setInputValue = multiple ? '' : (_c = selectedOptions[0]) === null || _c === void 0 ? void 0 : _c.title;
        dispatch({
          type: 'COLLAPSE',
          inputValue: setInputValue,
          latestEvent: event
        });
      }
    }
  }), /*#__PURE__*/React.createElement(Combobox, Object.assign({
    id: comboboxId,
    containerRef: updateListBoxPosition,
    selectedOptions: selectedOptions,
    value: inputValue,
    expanded: expanded,
    multiple: multiple,
    "aria-activedescendant": state.focusedOption ? optionIdTemplate(state.focusedOption.value) : undefined,
    "aria-controls": listBoxId
  }, comboboxProps, {
    inputFieldRef: element => {
      reactUtils.assignInnerRefWithComponentRef(element, comboboxProps === null || comboboxProps === void 0 ? void 0 : comboboxProps.inputFieldRef, comboboxFieldRef);
    },
    onKeyDown: event => {
      var _a;
      (_a = comboboxProps === null || comboboxProps === void 0 ? void 0 : comboboxProps.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(comboboxProps, event);
      if (!event.preventHCREventHandling) {
        handleComboboxKeyDown(event);
      }
    },
    onChange: event => {
      var _a;
      (_a = comboboxProps === null || comboboxProps === void 0 ? void 0 : comboboxProps.onChange) === null || _a === void 0 ? void 0 : _a.call(comboboxProps, event);
      if (!event.preventHCREventHandling) {
        handleComboboxChange(event);
      }
    }
  })), /*#__PURE__*/React.createElement(ListBox, Object.assign({
    id: listBoxId,
    options: options,
    flatOptions: flatOptions,
    "aria-labelledby": comboboxLabelId,
    hidden: !expanded,
    kbNavigation: state.kbNavigation,
    multiple: multiple,
    style: {
      top: listBoxTop
    },
    selectedOptions: selectedOptions,
    tabIndex: -1,
    highlightQuery: inputValue,
    optionIdTemplate: optionIdTemplate
  }, listBoxProps, {
    extraClasses: bem.e('list-box', {}, listBoxProps === null || listBoxProps === void 0 ? void 0 : listBoxProps.extraClasses)
  }))));
}
Autocomplete.displayName = 'Autocomplete';
Autocomplete.propTypes = {
  extraClasses: PropTypes.string,
  id: PropTypes.string,
  expanded: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.arrayOf(ListBoxOptionPropType), PropTypes.arrayOf(ListBoxGroupPropType)]),
  value: PropTypes.arrayOf(ListBoxOptionPropType),
  defaultValue: PropTypes.arrayOf(ListBoxOptionPropType),
  inputValue: PropTypes.string,
  defaultInputValue: PropTypes.string,
  multiple: PropTypes.bool,
  debounce: PropTypes.number,
  listBoxProps: PropTypes.shape(AutocompleteListBoxPropTypes),
  comboboxProps: PropTypes.shape(AutocompleteComboboxPropTypes),
  onChange: PropTypes.func,
  onToggle: PropTypes.func,
  onDebounce: PropTypes.func
};
var autocomplete = Injecss(Autocomplete, styles);

export { Autocomplete, autocomplete as default };
